import { normalizeStoredImageSize } from 'src/componentes/formulario-global/FormularioGlobalResizableImage';
import {
  getQuestionOptionsOrDefault,
  normalizeFormularioGlobalQuestionType,
  normalizeQuestionCommentSettings,
  normalizeQuestionOptions,
  normalizeQuestionMatrixSettings,
  getQuestionMatrixValidationErrors,
  FORMULARIO_GLOBAL_QUESTION_TYPES,
} from 'src/componentes/formulario-global/FormularioGlobalQuestionTypes';
import {
  canConfigureQuestionBranching,
  isQuestionBranchingEnabled,
  getOrderedBranchingTargets,
  getQuestionBranchingValidationError,
} from 'src/componentes/formulario-global/formularioGlobalBranching';
import createGuid from 'src/utils/guid';

const VALIDATION_LABELS = {
  SECTION_TITLE_REQUIRED: 'labelTituloSecaoObrigatorio',
  QUESTION_TITLE_REQUIRED: 'labelTituloPerguntaObrigatorio',
};
const FORMULARIO_GLOBAL_CLIENT_ID_FIELD = '_formularioGlobalClientId';

const ROOT_ENTRY_TYPE = {
  SECTION: 'section',
  QUESTION: 'question',
};

const stripFormularioGlobalClientId = (item) => {
  const nextItem = { ...item };
  delete nextItem[FORMULARIO_GLOBAL_CLIENT_ID_FIELD];
  return nextItem;
};

const hasValue = (value) => String(value || '').trim().length > 0;

const countPerguntas = (formulario) => {
  if (!formulario) return 0;

  const perguntasSecoes = (formulario.secoes || []).reduce(
    (total, secao) => total + (secao.perguntas || []).length,
    0
  );

  return perguntasSecoes + (formulario.perguntas || []).length;
};

const hasValidationErrors = (errors) =>
  Object.keys(errors?.sections || {}).length > 0 ||
  Object.keys(errors?.questions || {}).length > 0;

const getFormularioValidationToastError = (errors) =>
  [
    ...Object.values(errors?.questions || {}),
    ...Object.values(errors?.sections || {}).flatMap((section) =>
      Object.values(section.questions || {})
    ),
  ]
    .map((question) => question.ramificacao)
    .find((error) => error?.toast);

const getPerguntaValidationErrors = (
  pergunta,
  branchingTargets,
  randomizarPerguntas
) => {
  const branchingError = randomizarPerguntas
    ? null
    : getQuestionBranchingValidationError(pergunta, branchingTargets);
  return {
    ...(!hasValue(pergunta.titulo)
      ? { titulo: VALIDATION_LABELS.QUESTION_TITLE_REQUIRED }
      : {}),
    ...getQuestionMatrixValidationErrors(pergunta),
    ...(branchingError ? { ramificacao: branchingError } : {}),
  };
};

const getFormularioValidationErrors = (formulario, randomizarPerguntas) => {
  const branchingTargets = getOrderedBranchingTargets(formulario);
  const errors = {
    sections: {},
    questions: {},
  };

  (formulario.secoes || []).forEach((secao, sectionIndex) => {
    const sectionErrors = {};

    if (!hasValue(secao.titulo)) {
      sectionErrors.titulo = VALIDATION_LABELS.SECTION_TITLE_REQUIRED;
    }

    (secao.perguntas || []).forEach((pergunta, questionIndex) => {
      const questionErrors = getPerguntaValidationErrors(
        pergunta,
        branchingTargets,
        randomizarPerguntas
      );
      if (Object.keys(questionErrors).length > 0) {
        sectionErrors.questions = {
          ...(sectionErrors.questions || {}),
          [questionIndex]: questionErrors,
        };
      }
    });

    if (sectionErrors.titulo || sectionErrors.questions) {
      errors.sections[sectionIndex] = sectionErrors;
    }
  });

  (formulario.perguntas || []).forEach((pergunta, questionIndex) => {
    const questionErrors = getPerguntaValidationErrors(
      pergunta,
      branchingTargets,
      randomizarPerguntas
    );
    if (Object.keys(questionErrors).length > 0) {
      errors.questions[questionIndex] = questionErrors;
    }
  });

  return errors;
};

const normalizePerguntas = (perguntas = [], intl) =>
  perguntas.map((pergunta, index) => {
    const imageSize = normalizeStoredImageSize(
      pergunta.imagemLargura,
      pergunta.imagemAltura
    );
    const tipo = normalizeFormularioGlobalQuestionType(
      pergunta.tipo || pergunta.tipoPergunta
    );
    const normalizedQuestion = normalizeQuestionMatrixSettings(
      normalizeQuestionCommentSettings({
        ...pergunta,
        tipo,
        permitirComentario: !!pergunta.permitirComentario,
        opcoes: getQuestionOptionsOrDefault(tipo, pergunta.opcoes || [], intl),
      })
    );

    return {
      ...normalizedQuestion,
      id: pergunta.id || 0,
      guid: pergunta.guid || createGuid(),
      titulo: pergunta.titulo ?? '',
      tipo,
      obrigatoria: !!pergunta.obrigatoria,
      aplicarRamificacao: isQuestionBranchingEnabled({ ...pergunta, tipo }),
      arquivoSistemaId: pergunta.arquivoSistemaId || null,
      imagem: pergunta.imagem || null,
      imagemLargura: imageSize.width,
      imagemAltura: imageSize.height,
      ordem: pergunta.ordem || index + 1,
    };
  });

const normalizeFormulario = (formulario, intl, tipoFormulario) => {
  const normalized = formulario || {};

  return {
    ...normalized,
    id: normalized.id || 0,
    tipoFormulario: normalized.tipoFormulario || tipoFormulario,
    titulo: normalized.titulo || '',
    descricao: normalized.descricao || '',
    ativo: normalized.ativo ?? true,
    bloqueado: !!normalized.bloqueado,
    usos: normalized.usos || [],
    secoes: (normalized.secoes || []).map((secao, index) => {
      const imageSize = normalizeStoredImageSize(
        secao.imagemLargura,
        secao.imagemAltura
      );

      return {
        ...secao,
        id: secao.id || 0,
        guid: secao.guid || createGuid(),
        titulo: secao.titulo || '',
        descricao: secao.descricao || '',
        obrigatoria: !!secao.obrigatoria,
        arquivoSistemaId: secao.arquivoSistemaId || null,
        imagem: secao.imagem || null,
        imagemLargura: imageSize.width,
        imagemAltura: imageSize.height,
        perguntas: normalizePerguntas(secao.perguntas || [], intl),
        ordem: secao.ordem || index + 1,
      };
    }),
    perguntas: normalizePerguntas(normalized.perguntas || [], intl),
  };
};

const serializeOpcoes = (opcoes = []) =>
  normalizeQuestionOptions(opcoes).map((opcao, index) => ({
    ...opcao,
    texto: opcao.texto ?? '',
    valor: String(opcao.valor ?? index + 1),
    ordem: index + 1,
  }));

const serializePergunta = (pergunta, intl) => {
  const tipo = normalizeFormularioGlobalQuestionType(
    pergunta.tipo || pergunta.tipoPergunta
  );
  const serializedPergunta = normalizeQuestionMatrixSettings(
    normalizeQuestionCommentSettings({
      ...stripFormularioGlobalClientId(pergunta),
      tipo,
    })
  );

  return {
    ...serializedPergunta,
    aplicarRamificacao: isQuestionBranchingEnabled(serializedPergunta),
    guid: pergunta.guid || null,
    titulo: pergunta.titulo ?? '',
    tipo,
    obrigatoria: !!pergunta.obrigatoria,
    ...(tipo === FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO
      ? {
          linhas: serializedPergunta.linhas.map(({ id, texto, ordem }) => ({
            id,
            texto: String(texto ?? '').trim(),
            ordem,
          })),
          colunas: serializedPergunta.colunas.map(({ id, texto, ordem }) => ({
            id,
            texto: String(texto ?? '').trim(),
            ordem,
          })),
          permitirMultiplaSelecaoPorLinha:
            serializedPergunta.permitirMultiplaSelecaoPorLinha,
        }
      : {}),
    opcoes: serializeOpcoes(
      getQuestionOptionsOrDefault(tipo, serializedPergunta.opcoes || [], intl)
    ).map((option) =>
      canConfigureQuestionBranching(tipo)
        ? option
        : { ...option, destinoRamificacao: null, destinoRamificacaoGuid: null }
    ),
  };
};

const serializePerguntas = (perguntas = [], intl) =>
  perguntas.map((pergunta, index) => ({
    ...serializePergunta(pergunta, intl),
    ordem: index + 1,
  }));

const serializeFormulario = (
  formulario,
  targetEntidadeId,
  intl,
  tipoFormulario
) => {
  const normalized = normalizeFormulario(formulario, intl, tipoFormulario);

  const orderedRootEntries = [
    ...(normalized.secoes || []).map((secao) => ({
      type: ROOT_ENTRY_TYPE.SECTION,
      ordem: secao.ordem || 0,
      secao,
    })),
    ...(normalized.perguntas || []).map((pergunta) => ({
      type: ROOT_ENTRY_TYPE.QUESTION,
      ordem: pergunta.ordem || 0,
      pergunta,
    })),
  ].sort((a, b) => a.ordem - b.ordem);

  const secoes = [];
  const perguntas = [];

  orderedRootEntries.forEach((rootEntry, index) => {
    const ordem = index + 1;

    if (rootEntry.type === ROOT_ENTRY_TYPE.SECTION) {
      secoes.push({
        ...stripFormularioGlobalClientId(rootEntry.secao),
        perguntas: serializePerguntas(rootEntry.secao.perguntas || [], intl),
        ordem,
      });

      return;
    }

    perguntas.push({
      ...serializePergunta(rootEntry.pergunta, intl),
      ordem,
    });
  });

  return {
    ...normalized,
    tipoFormulario: tipoFormulario || normalized.tipoFormulario,
    entidadeId: targetEntidadeId,
    secoes,
    perguntas,
  };
};

export {
  countPerguntas,
  getFormularioValidationErrors,
  getFormularioValidationToastError,
  hasValidationErrors,
  normalizeFormulario,
  serializeFormulario,
};
